// RecomendacaoCard.js
import React from 'react';
import PropTypes from 'prop-types';
import {
 Card,
 CardContent,
 CardActions,
 Typography,
 Button,
 Box,
 LinearProgress,
 Chip
} from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';

const RecomendacaoNivelBadge = ({ nivel }) => {
 const getBadgeStyle = (nivel) => {
   switch (nivel) {
     case 'Excelente Escolha! Este curso combina perfeitamente com seu perfil acadêmico':
       return {
         backgroundColor: '#4caf50',
         color: 'white'
       };
     case 'Boa Opção! Este curso alinha-se bem com suas habilidades':
       return {
         backgroundColor: '#2196f3',
         color: 'white'
       };
     case 'Opção Considerável - Você tem potencial, mas pode exigir mais dedicação':
       return {
         backgroundColor: '#ff9800',
         color: 'white'
       };
     default:
       return {
         backgroundColor: '#f44336',
         color: 'white'
       };
   }
 };

 return (
   <Box sx={{ 
     ...getBadgeStyle(nivel),
     padding: '8px 16px',
     borderRadius: '20px',
     marginBottom: '16px',
     display: 'inline-block',
     fontWeight: 'bold',
     width: '100%',
     textAlign: 'center'
   }}>
     <Typography variant="subtitle1" sx={{ color: 'white' }}>
       {nivel}
     </Typography>
   </Box>
 );
};

const RecomendacaoCard = ({ recomendacao, onPreInscricao }) => {
 const {
   curso,
   pontuacaoAcademica = 0,
   pontuacaoGeografica = 0,
   nivel,
   justificativa,
 } = recomendacao;

 const cursoNome = curso?.nome || 'Curso não disponível';
 const instituicaoNome = curso?.instituicao?.nome || 'Instituição não disponível';

 const getColorScheme = (nivel) => {
   switch (nivel) {
     case 'Excelente Escolha! Este curso combina perfeitamente com seu perfil acadêmico':
       return {
         headerBg: '#e8f5e9',
         headerText: '#2e7d32',
         institutionBg: '#c8e6c9',
         institutionText: '#1b5e20',
         progressBar: '#43a047',
         cardBorder: '#4caf50'
       };
     case 'Boa Opção! Este curso alinha-se bem com suas habilidades':
       return {
         headerBg: '#e3f2fd',
         headerText: '#1976d2',
         institutionBg: '#bbdefb',
         institutionText: '#0d47a1',
         progressBar: '#1e88e5',
         cardBorder: '#2196f3'
       };
     case 'Opção Considerável - Você tem potencial, mas pode exigir mais dedicação':
       return {
         headerBg: '#fff3e0',
         headerText: '#e65100',
         institutionBg: '#ffe0b2',
         institutionText: '#ef6c00',
         progressBar: '#fb8c00',
         cardBorder: '#ff9800'
       };
     default:
       return {
         headerBg: '#ffebee',
         headerText: '#c62828',
         institutionBg: '#ffcdd2',
         institutionText: '#b71c1c',
         progressBar: '#e53935',
         cardBorder: '#f44336'
       };
   }
 };

 const colorScheme = getColorScheme(nivel);

 return (
   <Card sx={{ 
     mb: 2, 
     borderRadius: '12px', 
     overflow: 'hidden',
     border: `2px solid ${colorScheme.cardBorder}`,
     boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
   }}>
     <Box sx={{ 
       backgroundColor: colorScheme.headerBg,
       padding: 2,
     }}>
       <Typography variant="h5" sx={{ 
         color: colorScheme.headerText,
         fontWeight: 'bold',
         mb: 2
       }}>
         {cursoNome}
       </Typography>

       <Box sx={{ 
         backgroundColor: colorScheme.institutionBg,
         padding: 1.5,
         borderRadius: '8px',
         display: 'flex',
         alignItems: 'center'
       }}>
         <SchoolIcon sx={{ mr: 1, color: colorScheme.institutionText }} />
         <Typography variant="subtitle1" sx={{
           color: colorScheme.institutionText,
           fontWeight: 'bold'
         }}>
           {instituicaoNome}
         </Typography>
       </Box>
     </Box>

     <CardContent>
       <RecomendacaoNivelBadge nivel={nivel} />

       <Box sx={{ mb: 3 }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
           <Typography><strong>Compatibilidade Acadêmica</strong></Typography>
           <Typography>{Math.round(pontuacaoAcademica)}%</Typography>
         </Box>
         <LinearProgress
           variant="determinate"
           value={Number(pontuacaoAcademica)}
           sx={{ 
             mb: 2,
             height: 8,
             borderRadius: 4,
             backgroundColor: '#e0e0e0',
             '& .MuiLinearProgress-bar': {
               backgroundColor: colorScheme.progressBar
             }
           }}
         />

         <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
           <Typography><strong>Compatibilidade Geográfica</strong></Typography>
           <Typography>{Math.round(pontuacaoGeografica)}%</Typography>
         </Box>
         <LinearProgress
           variant="determinate"
           value={Number(pontuacaoGeografica)}
           sx={{ 
             height: 8,
             borderRadius: 4,
             backgroundColor: '#e0e0e0',
             '& .MuiLinearProgress-bar': {
               backgroundColor: colorScheme.progressBar
             }
           }}
         />
       </Box>

       <Box sx={{ 
         backgroundColor: '#f5f5f5',
         padding: 2,
         borderRadius: '8px',
         mb: 2
       }}>
         <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
           {justificativa}
         </Typography>
       </Box>
     </CardContent>

     <CardActions sx={{ padding: 2 }}>
       <Button
         variant="contained"
         fullWidth
         sx={{
           backgroundColor: colorScheme.progressBar,
           '&:hover': {
             backgroundColor: colorScheme.institutionText,
           },
           borderRadius: '8px',
           padding: '12px',
           fontWeight: 'bold'
         }}
         onClick={() => onPreInscricao(recomendacao)}
       >
         Fazer Pré-Inscrição
       </Button>
     </CardActions>
   </Card>
 );
};

RecomendacaoCard.propTypes = {
 recomendacao: PropTypes.shape({
   curso: PropTypes.shape({
     nome: PropTypes.string,
     instituicao: PropTypes.shape({
       nome: PropTypes.string,
     }),
   }),
   pontuacaoAcademica: PropTypes.number,
   pontuacaoGeografica: PropTypes.number,
   nivel: PropTypes.string,
   justificativa: PropTypes.string,
 }).isRequired,
 onPreInscricao: PropTypes.func.isRequired,
};
RecomendacaoNivelBadge.propTypes = {
  nivel: PropTypes.string.isRequired
};

export default RecomendacaoCard;